/**
 * withRetry – lightweight retry helpers for axios and fetch.
 *
 * Retryable conditions:
 *   - No response at all (network / timeout)
 *   - HTTP 5xx responses (server-side transient errors)
 *
 * Not retried:
 *   - HTTP 4xx (client errors – retrying won't help)
 *   - AbortError / CanceledError (intentional cancel)
 *   - Odoo / third-party POST side effects (caller decides)
 */

import axios from 'axios';

const MAX_RETRIES = 3;
const BACKOFF_MS  = [500, 1000, 2000]; // delay before attempt 1, 2, 3

function isRetryable(error) {
  // Intentional abort – never retry
  if (
    error.name === 'CanceledError' ||
    error.name === 'AbortError'
  ) return false;

  // No response → network error → retry
  if (!error.response) return true;

  // Server error → retry; client error → do not
  return error.response.status >= 500;
}

/**
 * Drop-in replacement for axios calls with automatic retry.
 *
 * @param {import('axios').AxiosRequestConfig} config  – same as axios(config)
 * @returns {Promise<import('axios').AxiosResponse>}
 *
 * @example
 *   const res = await axiosWithRetry({ method: 'get', url: `${API}/materials` });
 *   const res = await axiosWithRetry({ method: 'post', url: `${API}/quote`, data: body });
 */
export async function axiosWithRetry(config) {
  let lastError;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      return await axios(config);
    } catch (err) {
      lastError = err;
      if (!isRetryable(err) || attempt === MAX_RETRIES) throw err;
      if (process.env.NODE_ENV !== 'production') console.warn(
        `[axiosWithRetry] attempt ${attempt + 1} failed (${err.message}), ` +
        `retrying in ${BACKOFF_MS[attempt]}ms…`
      );
      await new Promise(r => setTimeout(r, BACKOFF_MS[attempt]));
    }
  }
  throw lastError;
}

/**
 * Drop-in wrapper for native fetch() with automatic retry.
 *
 * @param {string} url
 * @param {RequestInit} [options]
 * @returns {Promise<Response>}
 */
export async function fetchWithRetry(url, options = {}) {
  let lastError;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const res = await fetch(url, options);
      // Treat 5xx as retryable (need to clone first so body stays readable)
      if (res.status >= 500 && attempt < MAX_RETRIES) {
        if (process.env.NODE_ENV !== 'production') console.warn(
          `[fetchWithRetry] attempt ${attempt + 1} got ${res.status}, ` +
          `retrying in ${BACKOFF_MS[attempt]}ms…`
        );
        await new Promise(r => setTimeout(r, BACKOFF_MS[attempt]));
        continue;
      }
      return res;
    } catch (err) {
      lastError = err;
      // AbortError = intentional cancel, don't retry
      if (err.name === 'AbortError' || attempt === MAX_RETRIES) throw err;
      if (process.env.NODE_ENV !== 'production') console.warn(
        `[fetchWithRetry] attempt ${attempt + 1} failed (${err.message}), ` +
        `retrying in ${BACKOFF_MS[attempt]}ms…`
      );
      await new Promise(r => setTimeout(r, BACKOFF_MS[attempt]));
    }
  }
  throw lastError;
}
